import { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';
import { DateRangePicker as ReactDatesDateRangePicker } from 'react-dates';
import { useTranslation } from 'react-i18next';

import { DateRange } from '~interfaces/dateRanges';

import { useStyles } from './dateRangePickerStyle';

interface DateRangePickerProps {
	value: Partial<DateRange>;
	showClearDates?: boolean;
	showDefaultInputIcon?: boolean;
	minDate?: Date;
	maxDate?: Date;
	onDatesChange?: (value: { start?: Date; end?: Date }) => void;
}

/**
 * A date range picker with controlled and uncontrolled input.
 * A more reusable version of the ./components/ui/datepicker version
 */
const DateRangePicker = ({
	value: valueFromProps,
	minDate,
	maxDate,
	onDatesChange,
	...reactDatesDateRangePickerProps
}: DateRangePickerProps) => {
	const { i18n, t } = useTranslation();
	const classes = useStyles();

	useEffect(() => {
		if (i18n.language.includes('nl')) {
			moment.locale('nl-NL');
		} else if (i18n.language.includes('de')) {
			moment.locale('de-DE');
		} else {
			moment.locale('en-US');
		}
	}, [i18n.language]);

	const [focusedInput, setFocusedInput] = useState<string | null>(null);

	const isInputControlled = valueFromProps !== undefined;
	const [internalValue, setInternalValue] = useState<{ start?: Moment; end?: Moment }>({});
	const value =
		isInputControlled ?
			{
				start: valueFromProps.start != null ? moment(valueFromProps.start) : undefined,
				end: valueFromProps.end != null ? moment(valueFromProps.end) : undefined,
			}
		:	internalValue;

	const handleDatesChange = (value: { startDate?: Moment; endDate?: Moment }) => {
		if (!isInputControlled) {
			setInternalValue({ start: value.startDate, end: value.endDate });
		}

		onDatesChange?.({
			start: value.startDate?.startOf('day').toDate(),
			end: value.endDate?.endOf('day').toDate(),
		});
	};

	return (
		<div className={classes.root}>
			<ReactDatesDateRangePicker
				{...reactDatesDateRangePickerProps}
				displayFormat={() => 'D-M-YYYY'}
				startDate={value.start}
				endDate={value.end}
				className={classes}
				focusedInput={focusedInput}
				onFocusChange={(value: 'startDate' | 'endDate' | null) => setFocusedInput(value)}
				onDatesChange={handleDatesChange}
				startDatePlaceholderText={t('ui.label.startDate')}
				endDatePlaceholderText={t('ui.label.endDate')}
				showClearDates={true}
				showDefaultInputIcon={true}
				isOutsideRange={(date: Date, e) => {
					return (maxDate != null && date >= maxDate) || (minDate != null && date < minDate);
				}}
				// isDayBlocked={(date: Date) =>
				// 	(maxDate != null && date > maxDate) || (minDate != null && date < minDate)
				// }
			/>
		</div>
	);
};

export default DateRangePicker;
